import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { graphqlClient } from "./graphql";
import { graphql } from "@/__generated__";
import { languageDisplayName, languageEndonym, sortedLanguageOptions } from "@/lib/languageNames";

export { languageDisplayName, languageEndonym, sortedLanguageOptions };

// Language reference data and the student's native language (L1). The server
// owns which rows exist and which are teachable (Language.teachable): the six
// language pickers read Query.languages (teachable only), the native-language
// picker reads Query.nativeLanguageOptions (every non-signed row, so the nine
// UI locales nobody teaches — Italian, Dutch, … — are still selectable as L1).

export type LanguageOption = { id: string; code: string; name: string };

const NATIVE_LANGUAGE_OPTIONS_QUERY = graphql(`
  query NativeLanguageOptions {
    nativeLanguageOptions { id name code }
  }
`);

export function useNativeLanguageOptions() {
  return useQuery({
    queryKey: ["nativeLanguageOptions"],
    queryFn: async (): Promise<LanguageOption[]> => {
      const data = await graphqlClient.request(NATIVE_LANGUAGE_OPTIONS_QUERY);
      return data.nativeLanguageOptions;
    },
    // Reference data — never changes within a session.
    staleTime: Infinity,
  });
}

const TEACHABLE_LANGUAGES_QUERY = graphql(`
  query TeachableLanguages {
    languages { id name code }
  }
`);

// Same key and row shape as the onboarding / Find-tutors "Languages" query, so
// the two share one cache entry.
export function useTeachableLanguages(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["languages"],
    queryFn: async (): Promise<LanguageOption[]> => {
      const data = await graphqlClient.request(TEACHABLE_LANGUAGES_QUERY);
      return data.languages;
    },
    enabled: opts.enabled ?? true,
  });
}

// Display label for a native-language option: the language's own name for
// itself for EVERY row ("Français", "日本語", "العربية"), English name when
// Intl has nothing. A student picks their mother tongue; it should read as one.
export function nativeLanguageLabel(language: { code: string; name: string }): string {
  return languageEndonym(language.code, language.name);
}

// Onboarding default: the row matching the current UI locale (base code, so
// "zh-CN" finds "zh"), else English, else nothing yet (options still loading).
export function defaultNativeLanguageId(options: LanguageOption[], uiLanguage: string): string {
  const base = (uiLanguage ?? "").toLowerCase().split("-")[0];
  const match = options.find((l) => l.code === base) ?? options.find((l) => l.code === "en");
  return match?.id ?? "";
}

// What the SERVER would use as this student's L1 right now, and whether it only
// guessed it (#878). Deliberately its own slim document, never a field on
// ME_QUERY: a new field there signs every user out during a rolling deploy.
//
// Read this instead of `me.studentProfile.nativeLanguage` anywhere the answer
// decides what the student is shown. The raw column is null for everyone who
// never answered, so a check keyed on it disagrees with the server's own
// capture guard — which is exactly how the New deck dialog ended up staying
// silent for the one student it was written to warn.
const STUDENT_L1_STATUS_QUERY = graphql(`
  query StudentL1Status {
    me {
      id
      studentProfile { id l1Status { ambiguous language { id code name } } }
    }
  }
`);

export type StudentL1Status = { ambiguous: boolean; language: LanguageOption };

export function useStudentL1Status(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["studentL1Status"],
    queryFn: async (): Promise<StudentL1Status | null> => {
      const data = await graphqlClient.request(STUDENT_L1_STATUS_QUERY);
      return data.me?.studentProfile?.l1Status ?? null;
    },
    enabled: opts.enabled ?? true,
  });
}

const UPDATE_STUDENT_NATIVE_LANGUAGE_MUTATION = graphql(`
  mutation UpdateStudentNativeLanguage($nativeLanguageId: ID!) {
    updateStudentNativeLanguage(input: { nativeLanguageId: $nativeLanguageId }) {
      studentProfile { id nativeLanguage { id code name } }
      errors
      errorCode
    }
  }
`);

export function useUpdateStudentNativeLanguage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nativeLanguageId: string) =>
      graphqlClient.request(UPDATE_STUDENT_NATIVE_LANGUAGE_MUTATION, { nativeLanguageId }),
    onSuccess: (data) => {
      // A refused change (the language they are learning, an unknown id)
      // changed nothing; refetching three caches for it was noise.
      if (data.updateStudentNativeLanguage.errors.length > 0) return;
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["meStudentSettings"] });
      // Translations, dictionary lookups and seeded examples all follow the L1.
      qc.invalidateQueries({ queryKey: ["flashcards"] });
      // Answering is the only thing that clears `ambiguous` — without this the
      // dialog would keep asking a question the student has already answered.
      qc.invalidateQueries({ queryKey: ["studentL1Status"] });
    },
  });
}
